// Werkzeugkasten für den gemalten, prozeduralen Stil (Canvas 2D).
// Alles deterministisch über einen Seed, damit Screenshots stabil bleiben.
import { P } from './palette.ts';

export type Ctx = CanvasRenderingContext2D;

export class Rng {
  private s: number;
  constructor(seed: number | string) {
    let h = 2166136261;
    const str = String(seed);
    for (let i = 0; i < str.length; i++) h = Math.imul(h ^ str.charCodeAt(i), 16777619);
    this.s = h >>> 0 || 1;
  }
  next(): number {
    // mulberry32
    let t = (this.s += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
  range(a: number, b: number) {
    return a + (b - a) * this.next();
  }
  int(a: number, b: number) {
    return Math.floor(this.range(a, b + 1));
  }
  pick<T>(arr: readonly T[]): T {
    return arr[Math.floor(this.next() * arr.length)];
  }
  chance(p: number) {
    return this.next() < p;
  }
}

// ---------- Farben ----------

function hexToRgb(h: string): [number, number, number] {
  const n = parseInt(h.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function rgbToHex(r: number, g: number, b: number) {
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return '#' + c(r) + c(g) + c(b);
}
/** Mischung zweier Palettenfarben */
export function mix(a: string, b: string, t: number): string {
  const x = hexToRgb(a), y = hexToRgb(b);
  return rgbToHex(x[0] + (y[0] - x[0]) * t, x[1] + (y[1] - x[1]) * t, x[2] + (y[2] - x[2]) * t);
}
/** abdunkeln (amt<0, Richtung Nachtblau) oder aufhellen (amt>0, Richtung Bernstein-Hell) */
export function shade(c: string, amt: number): string {
  return amt < 0 ? mix(c, P.night0, -amt) : mix(c, P.amber3, amt);
}
export function rgba(c: string, a: number): string {
  const [r, g, b] = hexToRgb(c);
  return `rgba(${r},${g},${b},${a})`;
}

// ---------- Canvas ----------

export function makeCanvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.ceil(w));
  c.height = Math.max(1, Math.ceil(h));
  return c;
}

let noiseCanvas: HTMLCanvasElement | null = null;
function noiseTile(): HTMLCanvasElement {
  if (noiseCanvas) return noiseCanvas;
  const c = makeCanvas(128, 128);
  const ctx = c.getContext('2d')!;
  const img = ctx.createImageData(128, 128);
  const r = new Rng('noise');
  // helle und dunkle Körner mit Alpha – wirken per 'source-atop' nur auf vorhandene Pixel
  for (let i = 0; i < img.data.length; i += 4) {
    const v = r.next() - 0.5;
    img.data[i] = img.data[i + 1] = img.data[i + 2] = v > 0 ? 255 : 0;
    img.data[i + 3] = Math.abs(v) * 2 * 255;
  }
  ctx.putImageData(img, 0, 0);
  noiseCanvas = c;
  return c;
}

/** feines Korn über eine Fläche (malt im aktuellen Clip) */
export function grain(ctx: Ctx, x: number, y: number, w: number, h: number, strength = 0.08) {
  ctx.save();
  ctx.globalAlpha = strength * 0.9;
  ctx.globalCompositeOperation = 'source-atop';
  ctx.fillStyle = ctx.createPattern(noiseTile(), 'repeat')!;
  ctx.fillRect(x, y, w, h);
  ctx.restore();
}

export function vgrad(ctx: Ctx, x: number, y: number, w: number, h: number, stops: [number, string][]) {
  const g = ctx.createLinearGradient(x, y, x, y + h);
  for (const [o, c] of stops) g.addColorStop(o, c);
  ctx.fillStyle = g;
  ctx.fillRect(x, y, w, h);
}

export function hgrad(ctx: Ctx, x: number, y: number, w: number, h: number, stops: [number, string][]) {
  const g = ctx.createLinearGradient(x, y, x + w, y);
  for (const [o, c] of stops) g.addColorStop(o, c);
  ctx.fillStyle = g;
  ctx.fillRect(x, y, w, h);
}

export function radial(ctx: Ctx, x: number, y: number, r: number, stops: [number, string][]) {
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  for (const [o, c] of stops) g.addColorStop(o, c);
  ctx.fillStyle = g;
  ctx.fillRect(x - r, y - r, 2 * r, 2 * r);
}

/** weiches Licht (additiv) */
export function glow(ctx: Ctx, x: number, y: number, r: number, color: string, a = 0.6) {
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  radial(ctx, x, y, r, [
    [0, rgba(color, a)],
    [0.35, rgba(color, a * 0.45)],
    [1, rgba(color, 0)],
  ]);
  ctx.restore();
}

/** Pinseltupfer: viele halbtransparente Ellipsen für eine gemalte Oberfläche */
export function dabs(
  ctx: Ctx,
  rng: Rng,
  x: number,
  y: number,
  w: number,
  h: number,
  colors: string[],
  opts: { count?: number; size?: [number, number]; alpha?: [number, number]; stretch?: number; angle?: number } = {},
) {
  const count = opts.count ?? Math.round((w * h) / 900);
  const [s0, s1] = opts.size ?? [6, 22];
  const [a0, a1] = opts.alpha ?? [0.05, 0.16];
  const stretch = opts.stretch ?? 2.2;
  ctx.save();
  for (let i = 0; i < count; i++) {
    const px = x + rng.next() * w, py = y + rng.next() * h;
    const s = rng.range(s0, s1);
    ctx.globalAlpha = rng.range(a0, a1);
    ctx.fillStyle = rng.pick(colors);
    ctx.beginPath();
    ctx.ellipse(px, py, s * stretch, s, (opts.angle ?? 0) + rng.range(-0.25, 0.25), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

/** gemalte Linie: mehrere leicht versetzte, transparente Striche */
export function brushLine(ctx: Ctx, rng: Rng, x1: number, y1: number, x2: number, y2: number, color: string, width: number, passes = 3) {
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineCap = 'round';
  for (let i = 0; i < passes; i++) {
    ctx.globalAlpha = 0.35 + rng.next() * 0.3;
    ctx.lineWidth = width * rng.range(0.6, 1.2);
    const j = width * 0.4;
    ctx.beginPath();
    ctx.moveTo(x1 + rng.range(-j, j), y1 + rng.range(-j, j));
    const mx = (x1 + x2) / 2 + rng.range(-j, j), my = (y1 + y2) / 2 + rng.range(-j, j);
    ctx.quadraticCurveTo(mx, my, x2 + rng.range(-j, j), y2 + rng.range(-j, j));
    ctx.stroke();
  }
  ctx.restore();
}

export function roundRect(ctx: Ctx, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export function poly(ctx: Ctx, pts: number[]) {
  ctx.beginPath();
  ctx.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) ctx.lineTo(pts[i], pts[i + 1]);
  ctx.closePath();
}

/** Fläche mit Grundfarbe, Verlauf (oben hell -> unten dunkel), Tupfern und Korn */
export function paintedFill(
  ctx: Ctx,
  rng: Rng,
  path: () => void,
  bbox: [number, number, number, number],
  base: string,
  opts: { light?: number; dark?: number; dabsCount?: number; grain?: number; vertical?: boolean } = {},
) {
  const [x, y, w, h] = bbox;
  ctx.save();
  path();
  ctx.clip();
  const stops: [number, string][] = [
    [0, shade(base, opts.light ?? 0.08)],
    [1, shade(base, -(opts.dark ?? 0.25))],
  ];
  if (opts.vertical === false) hgrad(ctx, x, y, w, h, stops);
  else vgrad(ctx, x, y, w, h, stops);
  dabs(ctx, rng, x, y, w, h, [shade(base, 0.15), shade(base, -0.2), base], {
    count: opts.dabsCount ?? Math.round((w * h) / 700),
    size: [Math.max(2, Math.min(w, h) * 0.02), Math.max(4, Math.min(w, h) * 0.08)],
  });
  grain(ctx, x, y, w, h, opts.grain ?? 0.1);
  ctx.restore();
}

/** Holzmaserung in einem Rechteck */
export function woodGrain(ctx: Ctx, rng: Rng, x: number, y: number, w: number, h: number, base: string, horizontal = true) {
  ctx.save();
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.clip();
  ctx.fillStyle = base;
  ctx.fillRect(x, y, w, h);
  const lines = Math.max(3, Math.round((horizontal ? h : w) / 5));
  for (let i = 0; i < lines; i++) {
    const t = rng.next();
    ctx.strokeStyle = rng.chance(0.5) ? shade(base, -0.25) : shade(base, 0.12);
    ctx.globalAlpha = rng.range(0.15, 0.4);
    ctx.lineWidth = rng.range(0.6, 2);
    ctx.beginPath();
    if (horizontal) {
      const yy = y + t * h;
      ctx.moveTo(x, yy);
      const segs = 6;
      for (let s = 1; s <= segs; s++) ctx.lineTo(x + (w * s) / segs, yy + rng.range(-1.5, 1.5));
    } else {
      const xx = x + t * w;
      ctx.moveTo(xx, y);
      const segs = 6;
      for (let s = 1; s <= segs; s++) ctx.lineTo(xx + rng.range(-1.5, 1.5), y + (h * s) / segs);
    }
    ctx.stroke();
  }
  // Astlöcher
  if (rng.chance(0.4)) {
    ctx.globalAlpha = 0.5;
    ctx.fillStyle = shade(base, -0.35);
    ctx.beginPath();
    ctx.ellipse(x + rng.next() * w, y + rng.next() * h, horizontal ? 7 : 3, horizontal ? 3 : 7, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
  grain(ctx, x, y, w, h, 0.12);
}

/** weicher Schlagschatten unter Objekten */
export function contactShadow(ctx: Ctx, x: number, y: number, w: number, h: number, a = 0.45) {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(1, h / w);
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, w / 2);
  g.addColorStop(0, rgba(P.ink, a));
  g.addColorStop(1, rgba(P.ink, 0));
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(0, 0, w / 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** Kantenlicht: helle Kante oben, dunkle Kante unten (gemalter Look statt Outline) */
export function edgeLight(ctx: Ctx, x: number, y: number, w: number, h: number, base: string, top = 0.25, bottom = 0.35) {
  ctx.save();
  ctx.fillStyle = shade(base, top);
  ctx.globalAlpha = 0.7;
  ctx.fillRect(x, y, w, Math.max(1.5, h * 0.04));
  ctx.fillStyle = shade(base, -bottom);
  ctx.fillRect(x, y + h - Math.max(2, h * 0.05), w, Math.max(2, h * 0.05));
  ctx.restore();
}

export function text(ctx: Ctx, s: string, x: number, y: number, font: string, color: string, align: CanvasTextAlign = 'center') {
  ctx.save();
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textAlign = align;
  ctx.textBaseline = 'middle';
  ctx.fillText(s, x, y);
  ctx.restore();
}
